import type {
  AssignmentStudentStatus,
  AssignmentSubTask,
  ProjectAssignmentItem,
  RoutineAssignmentItem,
  RoutineDateBucket,
} from '../types/assignmentPointsNote';
import { calculateRoutineDates } from './assignmentPointsNoteProgress';

function statusKeyParts(
  key: string,
  fallbackStudentId?: string
): { subId: string; studentId: string } {
  const sep = key.indexOf(':');
  if (sep < 0) return { subId: key, studentId: fallbackStudentId ?? '' };
  return { subId: key.slice(0, sep), studentId: key.slice(sep + 1) };
}

export function pruneProjectStudentStatuses(
  prevBy: Record<string, AssignmentStudentStatus>,
  subTasks: AssignmentSubTask[],
  assignedMemberIds: string[]
): Record<string, AssignmentStudentStatus> {
  const subIds = new Set(subTasks.map((s) => s.id));
  const members = new Set(assignedMemberIds);
  const out: Record<string, AssignmentStudentStatus> = {};
  Object.entries(prevBy).forEach(([k, v]) => {
    if (!v) return;
    const { subId, studentId } = statusKeyParts(k, v.studentId);
    if (subIds.has(subId) && members.has(studentId)) out[k] = v;
  });
  return out;
}

export function pruneRoutineDateStatuses(
  prevBuckets: RoutineDateBucket[],
  startDate: string,
  dueDate: string,
  routineDays: number[],
  assignedMemberIds: string[]
): RoutineDateBucket[] {
  const dates = calculateRoutineDates(startDate, dueDate, routineDays);
  const dateSet = new Set(dates);
  const members = new Set(assignedMemberIds);
  const byDate = new Map(prevBuckets.map((b) => [b.date, b]));
  const filterMembers = (statuses: AssignmentStudentStatus[] | undefined) =>
    (statuses ?? []).filter((s) => members.has(s.studentId));
  const routineBuckets = dates.map((date) => {
    const prev = byDate.get(date);
    return {
      date,
      studentStatuses: filterMembers(prev?.studentStatuses),
      isCustomDate: false as const,
    };
  });
  const customBuckets = prevBuckets
    .filter((b) => b.isCustomDate && !dateSet.has(b.date))
    .map((b) => ({
      date: b.date,
      studentStatuses: filterMembers(b.studentStatuses),
      isCustomDate: true as const,
    }));
  return [...routineBuckets, ...customBuckets];
}

export function projectEditWouldDeleteRecords(
  prev: ProjectAssignmentItem,
  next: ProjectAssignmentItem
): boolean {
  const kept = pruneProjectStudentStatuses(
    prev.studentStatusesBySubTask,
    next.subTasks,
    next.assignedMemberIds
  );
  return Object.entries(prev.studentStatusesBySubTask).some(
    ([k, v]) => !!v && kept[k] == null
  );
}

export function routineEditWouldDeleteRecords(
  prev: RoutineAssignmentItem,
  next: RoutineAssignmentItem
): boolean {
  const kept = pruneRoutineDateStatuses(
    prev.dateStatuses,
    next.startDate,
    next.dueDate,
    next.routineDays,
    next.assignedMemberIds
  );
  const keptKeys = new Set<string>();
  for (const b of kept) {
    for (const s of b.studentStatuses) {
      keptKeys.add(`${b.date}:${s.studentId}`);
    }
  }
  for (const b of prev.dateStatuses) {
    for (const s of b.studentStatuses ?? []) {
      if (!keptKeys.has(`${b.date}:${s.studentId}`)) return true;
    }
  }
  return false;
}
